import {
	AuthResponseDto,
	RefreshDto,
	SignInWithEmailDto,
	IdDto,
	AuthVerifyDto,
	SignInWithPhoneDto,
	SignUpWithEmailDto,
	UserDto,
	EmailDto,
	ChangeUserLoginRequest,
	ChangeUserLoginDto,
	RestorePasswordDto,
	ChangeUserPasswordDto,
	ValidationDto,
	UserInfoDto,
	CreateUserDto,
} from '@families-link/shared';
import api from '.';
import { signOutAction, updateSessionAction } from '../../modules/auth';

const authApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProfile: builder.query<UserDto, void>({
			query: () => ({
				url: `auth/profile`,
			}),
			providesTags: ['UserProfile'],
		}),
		sendOtp: builder.mutation<IdDto, AuthVerifyDto>({
			query: (data) => ({
				url: 'auth/send-otp',
				method: 'POST',
				body: data,
			}),
		}),
		refresh: builder.mutation<AuthResponseDto, RefreshDto>({
			query: (data) => ({
				url: 'auth/refresh',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		authWithGoogleToken: builder.mutation<AuthResponseDto, string>({
			query: (idToken) => ({
				url: 'auth/google',
				method: 'POST',
				body: { idToken },
			}),
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const response = await queryFulfilled;
					dispatch(updateSessionAction(response.data));
				} catch {
					/* empty */
				}
			},
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		assignGoogleAccount: builder.mutation<void, string>({
			query: (idToken) => ({
				url: 'auth/google/assign',
				method: 'POST',
				body: { idToken },
			}),
			invalidatesTags: ['UserProfile'],
		}),
		unassignGoogleAccount: builder.mutation<void, void>({
			query: () => ({
				url: 'auth/google/unassign',
				method: 'POST',
			}),
			invalidatesTags: ['UserProfile'],
		}),
		registerWithEmail: builder.mutation<AuthResponseDto, SignUpWithEmailDto>({
			query: (data) => ({
				url: 'auth/sign-up/email',
				method: 'POST',
				body: data,
			}),
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const response = await queryFulfilled;
					dispatch(updateSessionAction(response.data));
				} catch {
					/* empty */
				}
			},
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		signInWithPhone: builder.mutation<AuthResponseDto, SignInWithPhoneDto>({
			query: (data) => ({
				url: `auth/sign-in/phone`,
				method: 'POST',
				body: data,
			}),
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const response = await queryFulfilled;
					dispatch(updateSessionAction(response.data));
				} catch {
					/* empty */
				}
			},
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		signInWithEmail: builder.mutation<AuthResponseDto, SignInWithEmailDto>({
			query: (data) => ({
				url: `auth/sign-in/email`,
				method: 'POST',
				body: data,
			}),
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const response = await queryFulfilled;
					dispatch(updateSessionAction(response.data));
				} catch {
					/* empty */
				}
			},
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		inviteRequest: builder.mutation<void, EmailDto>({
			query: (data: EmailDto) => ({
				url: `auth/send-invitation`,
				method: 'POST',
				body: data,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		validateInvitation: builder.query<ValidationDto, { token: string }>({
			query: ({ token }) => ({
				url: `auth/validate-invitation?token=${token}`,
				method: 'GET',
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		restorePasswordRequest: builder.mutation<void, EmailDto>({
			query: (data: EmailDto) => ({
				url: `auth/restore-password-request`,
				method: 'POST',
				body: data,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		restorePassword: builder.mutation<void, RestorePasswordDto & { token?: string }>({
			query: ({ token, ...data }: RestorePasswordDto & { token?: string }) => ({
				url: `auth/restore-password`,
				method: 'POST',
				body: data,
				...(token
					? {
							headers: {
								authorization: `Bearer ${token}`,
							},
					  }
					: {}),
			}),
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		changeLoginRequest: builder.mutation<IdDto | undefined, ChangeUserLoginRequest>({
			query: (data) => ({
				url: `auth/change-login-request`,
				method: 'POST',
				body: data,
			}),
		}),
		changeLogin: builder.mutation<void, ChangeUserLoginDto>({
			query: (data) => ({
				url: `auth/change-login`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['UserProfile'],
		}),
		changePassword: builder.mutation<void, ChangeUserPasswordDto>({
			query: (data) => ({
				url: `auth/change-password`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['UserProfile'],
		}),
		signOut: builder.mutation<void, string>({
			query: (refreshToken) => ({
				url: `auth/sign-out`,
				method: 'POST',
				body: {
					refreshToken,
				},
			}),
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					dispatch(signOutAction());
				} catch {
					/* empty */
				}
			},
		}),
		generateRegistrationLink: builder.mutation<{ link: string }, { districtId: string }>({
			query: ({ districtId }) => ({
				url: `auth/generate-link?districtId=${districtId}`,
				method: 'GET',
			}),
		}),
		registerUser: builder.mutation<UserDto, CreateUserDto>({
			query: (data: CreateUserDto) => ({
				url: '/auth/register',
				method: 'POST',
				body: data,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),

		validateRegistrationLink: builder.query<ValidationDto, { token: string }>({
			query: ({ token }) => ({
				url: `auth/validate-registration-link?token=${token}`,
				method: 'GET',
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
	}),
});

export default authApi;

export const {
	useGetProfileQuery,
	useSendOtpMutation,
	useSignInWithPhoneMutation,
	useSignInWithEmailMutation,
	useRegisterWithEmailMutation,
	useAuthWithGoogleTokenMutation,
	useAssignGoogleAccountMutation,
	useUnassignGoogleAccountMutation,
	useRestorePasswordRequestMutation,
	useRestorePasswordMutation,
	useChangeLoginRequestMutation,
	useChangeLoginMutation,
	useChangePasswordMutation,
	useSignOutMutation,
	useInviteRequestMutation,
	useValidateInvitationQuery,
	useGenerateRegistrationLinkMutation,
	useRegisterUserMutation,
	useValidateRegistrationLinkQuery,
} = authApi;
